import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface YeonBiProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

export default function YeonBi({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: YeonBiProps) {
  if (!["BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const demoBtn =
    hasDemo?.(["yeon_bi", "yeonbi"]) ? (
      <button
        type="button"
        className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
        onClick={() => onWatchDemo?.(["yeon_bi", "yeonbi"])}
      >
        Watch Demonstration Video
      </button>
    ) : null;

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">YeonBi — Flying Swallow</h2>
        {demoBtn && <div className="mb-3">{demoBtn}</div>}
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="YeonBi">
            <TabsTrigger value="pattern-information" className={triggerClass}>
              Pattern Information
            </TabsTrigger>
            <TabsTrigger value="meaning" className={triggerClass}>
              Meaning
            </TabsTrigger>
            <TabsTrigger value="key-techniques" className={triggerClass}>
              Key Techniques
            </TabsTrigger>
            <TabsTrigger value="training-focus" className={triggerClass}>
              Training Focus
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="mb-4 text-gray-700">
                YeonBi, meaning &quot;Flying Swallow&quot;, is an advanced pattern that embodies the grace, speed, and precision of a swallow in
                flight. This pattern emphasizes fluid movements and swift transitions while maintaining technical accuracy.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="meaning" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
              <p className="text-gray-700">
                YeonBi represents the qualities of a flying swallow - grace, agility, and precision. The pattern teaches practitioners to move with
                the effortless efficiency of a bird in flight, combining speed with control and power with elegance.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Swift directional changes</li>
                <li>Flowing combination techniques</li>
                <li>Rapid striking sequences</li>
                <li>Agile footwork patterns</li>
                <li>Dynamic jumping techniques</li>
                <li>Precise targeting methods</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="training-focus" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Speed and agility</li>
                <li>Fluid movement transitions</li>
                <li>Technical precision</li>
                <li>Balance in motion</li>
                <li>Energy efficiency</li>
                <li>Spatial awareness</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div>
          {demoBtn}
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
          <p className="mb-4 text-gray-700">
            YeonBi, meaning &quot;Flying Swallow&quot;, is an advanced pattern that embodies the grace, speed, and precision of a swallow in flight.
            This pattern emphasizes fluid movements and swift transitions while maintaining technical accuracy.
          </p>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
          <p className="text-gray-700">
            YeonBi represents the qualities of a flying swallow - grace, agility, and precision. The pattern teaches practitioners to move with the
            effortless efficiency of a bird in flight, combining speed with control and power with elegance.
          </p>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Swift directional changes</li>
            <li>Flowing combination techniques</li>
            <li>Rapid striking sequences</li>
            <li>Agile footwork patterns</li>
            <li>Dynamic jumping techniques</li>
            <li>Precise targeting methods</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Speed and agility</li>
            <li>Fluid movement transitions</li>
            <li>Technical precision</li>
            <li>Balance in motion</li>
            <li>Energy efficiency</li>
            <li>Spatial awareness</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
